import React, { useCallback, useState, useEffect, useMemo } from 'react';

import { PlacedAnnotationSchema } from '@plangrid/scribble-client';
import styled from 'styled-components';

import { useCurrentProject } from '@adsk/alloy-provider';
import A from '@adsk/alloy-react-a';
import Divider from '@adsk/alloy-react-divider';
import { AlertWarningIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import UnstyledAnchor from '../../../UnstyledAnchor';
import {
  getThumbnail,
  dateToLocalString,
  sheetEntityLink,
} from '../helpers/utils';
import { Sheet } from '../types/types';

const Container = styled(UnstyledAnchor)`
  background-color: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.charcoal100};
  cursor: pointer;
  display: flex;
  height: 90px;
  justify-content: flex-start;
  align-items: center;
  padding: 10px;
  overflow: hidden;

  :last-child {
    border-bottom: none;
  }
`;

const ReferenceLink = styled.div`
  font-size: ${theme.typography.bodyMedium.fontSize};
  line-height: ${theme.typography.bodyMedium.lineHeight};
  a {
    text-decoration: none;
    display: inline-flex;
    span {
      margin-top: 2px;
      margin-left: 8px;
    }
  }
`;

const NullReferenceLink = styled(ReferenceLink)`
  line-height: ${theme.typography.bodyMedium.lineHeight};
  a {
    color: ${theme.colors.charcoal700};
  }
`;

const ThumbnailContainer = styled.div`
  margin-right: 10px;
  width: 40px;
  height: 40px;
  flex: none;
  display: flex;
  border: 1px solid ${theme.colors.charcoal100};
  background-color: ${theme.colors.charcoal050};
  justify-content: center;
  align-items: center;
`;

const ThumbnailPlaceholder = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${theme.colors.charcoal050};
`;

const MetadataContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BoldTitle = styled.span`
  font-weight: bold;
`;

const LeftContainer = styled.div`
  width: 70%;
  display: table;
  table-layout: fixed;
`;

const RightContainer = styled.div`
  display: flex;
`;

const MarkupsEntityBlockView = ({
  entity: sheetHistory,
  projectId,
  onReferenceClickAnalytics,
  sourceEntityType,
  markups,
  isLoading,
}: {
  entity: Sheet;
  projectId: string;
  onReferenceClickAnalytics: any;
  sourceEntityType?: string;
  markups: PlacedAnnotationSchema[];
  isLoading: boolean;
}) => {
  const [thumbnail, setThumbnail] = useState<string | null>();
  const currentProject = useCurrentProject();
  const scopes = currentProject?.scopes?.project;
  const url = useMemo(
    () =>
      projectId &&
      sheetEntityLink({ projectId, entityId: sheetHistory.sheetId, markups }),
    [markups, projectId, sheetHistory.sheetId],
  );
  const { T, locale } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`),
  );
  const countText = (count: number) => T.translate('MARKUP_TEXT', { count });
  const loadingText = () => T.translate('LOAD_MARKUPS');
  const noMarkupText = () =>
    T.translate('NO_MARKUPS', {
      type: (sourceEntityType || '').toUpperCase(),
      name: sheetHistory.name,
    });

  const fetchThumbnail = useCallback(async () => {
    const { bubbleUrn, bubbleViewableGuid } = sheetHistory;
    const blob = await getThumbnail(bubbleUrn, bubbleViewableGuid, scopes);
    setThumbnail(blob);
  }, [sheetHistory, scopes]);

  useEffect(() => {
    if (!thumbnail) {
      fetchThumbnail();
    }
    return () => {
      if (thumbnail) {
        URL.revokeObjectURL(thumbnail);
      }
    };
  }, [thumbnail, fetchThumbnail, sheetHistory]);

  const emptyView = () => (
    <Container>
      <Tooltip
        content={T.translate('NO_MARKUPS')}
        trigger={Tooltip.TRIGGER_EVENTS.CLICK}
        placement={Tooltip.PLACEMENTS.BOTTOM}
      >
        <NullReferenceLink>
          <A href={url}>
            <AlertWarningIcon />
            <span>{isLoading ? loadingText() : noMarkupText()}</span>
          </A>
        </NullReferenceLink>
      </Tooltip>
    </Container>
  );

  const pickerView = () => (
    <Container onClick={onReferenceClickAnalytics} href={url}>
      <ThumbnailContainer>
        {thumbnail ? (
          <img width="40" height="40" src={thumbnail} />
        ) : (
          <ThumbnailPlaceholder />
        )}
      </ThumbnailContainer>
      <MetadataContainer>
        <LeftContainer>
          <OverflowTooltip
            getTargetProps={() => ({
              style: { overflow: 'hidden', flex: 'auto' },
            })}
            content={sheetHistory.name}
          >
            <BoldTitle>{sheetHistory.name}</BoldTitle>
          </OverflowTooltip>
          <OverflowTooltip
            getTargetProps={() => ({
              style: {
                overflow: 'hidden',
                color: theme.colors.charcoal900,
              },
            })}
            content={
              sheetHistory.collection?.name ||
              dateToLocalString(markups[0]?.updated_at, locale)
            }
          >
            {sheetHistory.collection?.name ||
              dateToLocalString(markups[0]?.updated_at, locale)}
          </OverflowTooltip>
        </LeftContainer>
        <RightContainer>
          <Divider
            variant={Divider.VARIANTS.VERTICAL}
            style={{
              height: '40px',
              margin: '0px 8px',
              color: theme.colors.charcoal400,
            }}
          />
          <OverflowTooltip
            getTargetProps={() => ({
              style: {
                width: '100px',
                overflow: 'hidden',
                color: theme.colors.charcoal900,
                alignSelf: 'center',
              },
            })}
            content={`${countText(markups.length)}`}
          >
            {`${countText(markups.length)}`}
          </OverflowTooltip>
        </RightContainer>
      </MetadataContainer>
    </Container>
  );

  return (
    <React.Fragment>
      {!isLoading && markups.length > 0 ? pickerView() : emptyView()}
    </React.Fragment>
  );
};

export default MarkupsEntityBlockView;
